import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { applyIdentityLogin, getActivePortalHomePath } from '@/store/identitySlice';
import { identityApi } from '@/api/identity.api';
import { showApiError } from '@/utils/apiError';

function identityExchangeGuardKey(exchangeToken: string) {
  return `oauth:identity-exchange:${exchangeToken}`;
}

export function IdentityOAuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    const exchangeToken = params.get('exchangeToken');
    if (!exchangeToken) {
      navigate('/identity/login', { replace: true });
      return;
    }
    const guardKey = identityExchangeGuardKey(exchangeToken);
    if (sessionStorage.getItem(guardKey) === 'done' || exchangeStartedRef.current) {
      return;
    }
    exchangeStartedRef.current = true;
    sessionStorage.setItem(guardKey, 'pending');

    void (async () => {
      try {
        const result = await identityApi.ssoExchange(exchangeToken);
        sessionStorage.setItem(guardKey, 'done');
        dispatch(applyIdentityLogin(result));
        if ((result.availablePortals?.length ?? 0) > 1) {
          navigate('/identity/portals', { replace: true });
          return;
        }
        if (result.activeProfileId && result.activePortalCode && result.organizationId) {
          navigate(getActivePortalHomePath({
            identityId: result.identityId,
            identityUuid: result.identityUuid,
            email: result.email,
            activeProfileId: result.activeProfileId,
            activePortalCode: result.activePortalCode,
            organizationId: result.organizationId,
            organizationName: result.organizationName,
            permissions: result.permissions ?? [],
            availablePortals: result.availablePortals ?? [],
          }), { replace: true });
        } else {
          navigate('/identity/portals', { replace: true });
        }
      } catch (err) {
        sessionStorage.removeItem(guardKey);
        exchangeStartedRef.current = false;
        showApiError(err, 'SSO sign-in failed');
        navigate('/identity/login', { replace: true });
      }
    })();
  }, [dispatch, navigate, params]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
    </div>
  );
}
